import { useState, useContext, useEffect } from "react"
import { Link } from "react-router-dom"
import { Typography, Skeleton, List, Divider, Button } from "@mui/material"
import Box from '@mui/material/Box'

import { AppContext } from '/providers/app-context'

import ApplicationForm from './monthly-plan-application-form'

import { getMonthlyCreditPlans } from '/providers/studio-api'
import arrows from '/assets/images/single-arrow-up.svg'

const CreditPlan = () => {

  const { apiSource, apiKey, client } = useContext(AppContext)

  const [plans, setPlans] = useState([])
  const [selectedPlan, setSelectedPlan] = useState()
  const [applicationFormOpen,setApplicationFormOpen] = useState(false)

  useEffect(() => {
    getMonthlyPlans()
  }, [])

  const getMonthlyPlans = async () => {
    const p = await getMonthlyCreditPlans({ apiSource, apiKey })
    if (p.ok) {
      setPlans(p.plans)

      p.plans.map(p => {
        if (p.default === 1) setSelectedPlan(p)
      })
    }
  }

  const applyForPlan = async () => {
    console.log("Applying for ", selectedPlan.tariff_name)
    setApplicationFormOpen(true)
  }

  if (!client) return (
    <div className="flex flex-wrap gap-10 overflow-y-scroll p-5">
      <Skeleton variant="rounded" width={600} height={300} />
      <div className="flex flex-col gap-2 w-[500px] mr-20">
        <Skeleton variant="rounded" width={720} height={400} />
        <Skeleton variant="rounded" width={720} height={400} />
      </div>
    </div>
  )

  return (
    <div className='flex flex-col w-full ml-5 mt-5 p-20'>

        <div className='flex flex-col gap-5 p-10 items-center'>
          <div className='flex flex-col gap-5 p-10 items-center max-w-[800px]'>

            <Typography variant="h3">Monthly contract plans</Typography>

            <p>Monthly plans are designed for growing and high-volume use, offering lower per-credit pricing in exchange for a minimum monthly spend. As your usage increases, your effective cost per pass drops significantly compared to pay-as-you-go, making plans a more cost-efficient option for ongoing issuance, updates and notifications.</p>

            <p><Link to="support/history" className="link">Ask us about custom plans</Link></p>

          </div>
        </div>

      <div className='flex flex-row w-full justify-between'>
        {plans.map(plan => <Plan plan={plan} selectedPlan={selectedPlan} setSelectedPlan={setSelectedPlan} applyForPlan={applyForPlan}/>)}
      </div>

      <div className='px-10 text-center'>
        <a href="https://www.smartix.uk/pricing/" target="_blank" rel="noreferrer" className="link">See pricing page for credit cost breakdown</a>
      </div>

        <div className='flex flex-col gap-5 p-10 items-center'>
          <Typography variant="h3">Terms & Conditions</Typography>

          <ul className="list-disc list-outside ml-5 mb-5">
            <li>Our monthly rolling plan is billed <strong>in arrears</strong> each calendar month via <strong>direct debit</strong></li>
            <li>Payment is taken <strong>7 days after the invoice is raised</strong></li>
            <li>A <strong>minimum notice period</strong> applies</li>
            <li>A <strong>minimum monthly fee</strong> applies</li>
            <li>Approval is required for our monthly plan</li>
            <li>We may carry out a credit check</li>
            <li>Failed direct debit payments may result in your account being suspended</li>
          </ul>

          <p>Full terms & conditions can be found <a href="https://docs.smartix.uk/terms/monthly-terms/" target="_blank" className="link">here</a></p>
        </div>

      <ApplicationForm open={applicationFormOpen} setOpen={setApplicationFormOpen} client={client} tariff={selectedPlan?.tariff_name} plans={plans} />

    </div>
  )
}

export default CreditPlan

const Plan = ({ plan, selectedPlan, setSelectedPlan, applyForPlan }) => {

  const selected = selectedPlan === plan

  const sortedBands = plan.bands.sort((a, b) =>
    Math.round(a.cost_per_credit * 1e8) - Math.round(b.cost_per_credit * 1e8)
  )

  const classes = selected ? "" : "opacity-30"

  return (
    <div className={classes}>
      <div className='flex flex-col gap-5 p-2 items-center' onClick={() => setSelectedPlan(plan)}>
        <Typography variant="h3">{plan.tariff_name}</Typography>

        <List sx={{ width: 300 }}>
          <Item label="Minimum monthly spend" value={"£" + parseInt(plan.min_spend, 10).toLocaleString()} />
          <Item label="Notice period" value={plan.notice_period + " months"} />
          <Divider sx={{ mt: 2, mb: 2 }} />

          {sortedBands.map(band => (
            <>
              <Item
                label={
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      bgcolor: 'text.primary',
                      maskImage: `url(${arrows})`,
                      maskRepeat: 'no-repeat',
                      maskPosition: 'center',
                      maskSize: 'contain',
                      WebkitMaskImage: `url(${arrows})`,
                      WebkitMaskRepeat: 'no-repeat',
                      WebkitMaskPosition: 'center',
                      WebkitMaskSize: 'contain',
                      marginLeft : 6
                    }}
                  />
                }
                value={<span className="link">£ {band.cost_per_credit.toFixed(4)}</span>}
              />
              {band.min_credits > 0 && <div className="text-right w-[150px]">{(band.min_credits -1).toLocaleString()}  credits</div>}
            </>
          ))}
        </List>

        <Typography variant="h5">Example</Typography>

        <p>{plan.example}</p>

        <Button variant="outlined" onClick={applyForPlan}>Apply for {plan.tariff_name} plan</Button>
      </div>
    </div>
  )
}

const Item = ({ label, value }) => (
  <div className="flex flex-row justify-between w-full items-center">
    <div>{label}</div>
    <div className="font-bold">{value}</div>
  </div>
)
